import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import BackButton from "../components/common/BackButton";
import CustomiseSection from "../components/collection/CustomiseSection";

// 1. The Individual Product Card Component
const EmbroideryCard = ({ product, onViewClick }) => {
    const [loupePos, setLoupePos] = useState({ x: 0, y: 0 });
    const [imagePos, setImagePos] = useState({ x: 0, y: 0 });
    const imageWrapperRef = useRef(null);

    // This function calculates the mouse position relative to the image container
    // and updates the position of the loupe circle and the image inside it.
    const handleMouseMove = (e) => {
        const { left, top, width, height } =
            imageWrapperRef.current.getBoundingClientRect();

        // Mouse coordinates relative to the wrapper
        const x = e.clientX - left;
        const y = e.clientY - top;

        // Update Loupe Position (centered on mouse)
        setLoupePos({ x, y });

        // Update magnified image position *inside* the loupe.
        // New Formula:
        // We want the point (x, y) on the small image to correspond to the center of the loupe.
        // The large image is at -x * scale, -y * scale relative to the origin.
        // We add loupeRadius to center that 'view' in the loupe div.

        const scale = 2.8; // Matches CSS width: 250%
        const loupeRadius = 90; // Matches CSS width/height: 180px / 2 = 90

        setImagePos({
            x: -(x * scale) + loupeRadius,
            y: -(y * scale) + loupeRadius,
        });
    };

    return (
        <div className="pe-card-wrapper">
            <div className="pe-card pe-static-card">
                {/* --- Image Wrapper with Mouse Tracking --- */}
                <div
                    className="pe-image-wrapper"
                    ref={imageWrapperRef}
                    onMouseMove={handleMouseMove}
                // On mouse leave, maybe reset? Optional, but feels smoother if left as is.
                >
                    {/* The "Base" Photo (Flat) */}
                    <img
                        src={product.imageBase}
                        alt={product.title}
                        className="pe-base-image"
                    />

                    {/* The Magnifying Loupe Circle */}
                    <div
                        className="pe-loupe"
                        style={{ top: `${loupePos.y}px`, left: `${loupePos.x}px` }}
                    >
                        {/* The "Textured" Embroidery Image inside the loupe */}
                        <img
                            src={product.imageTexture}
                            alt="Embroidery Detail"
                            className="pe-loupe-image"
                            style={{
                                transform: `translate(${imagePos.x}px, ${imagePos.y}px)`,
                            }}
                        />
                    </div>
                </div>

                {/* --- Card Details --- */}
                <div className="pe-details">
                    {/* Replaced Category with ID */}
                    <span className="pe-category">PRODUCT ID: {product.id}</span>
                    <h3 className="pe-title">{product.title}</h3>

                    <div className="pe-button-container">
                        <button className="pe-btn" onClick={() => onViewClick(product)}>Click to View</button>
                    </div>
                </div>
            </div>
        </div>
    );
};




// 2. Main Container displaying the grid
export default function PhotoEmbroideryCollection() {
    const navigate = useNavigate();

    // DUMMY DATA - Replace with your real data
    // IMPT: Use real, high-res embroidery closeups for 'imageTexture'
    const products = [
        {
            id: 'PE-01',
            category: "Wedding Memories",
            title: "Forever Begins Here",
            price: "₹ 2,499",
            imageBase: "images/carousel-2.webp", // Flat photo
            imageTexture: "images/carousel-2.webp", // Detailed texture (stand-in)
            description: "Capture the start of your journey together with this elegant embroidery. Perfect for preserving the magic of your wedding day.",
            highlights: ["Hand-stitched details", "Premium wooden hoop", "Customizable date"]
        },
        {
            id: 'PE-02',
            category: "Family Heirlooms",
            title: "A Promise Woven in Time",
            price: "₹ 2,499",
            imageBase: "images/carousel-21.webp",
            imageTexture: "images/carousel-21.webp", // Texture stand-in
            description: "A beautiful representation of your vows, stitched to last forever. A cherished heirloom for generations to come.",
            highlights: ["Intricate threadwork", "Silk fabric base", "Personalized message option"]
        },
        {
            id: 'PE-03',
            category: "Wedding Memories",
            title: "Together, Always",
            price: "₹ 2,499",
            imageBase: "images/carousel-22.webp", // Flat photo
            imageTexture: "images/carousel-22.webp", // Detailed texture (stand-in)
            description: "Celebrate your union with a timeless piece of art. The perfect gift for anniversaries or weddings.",
            highlights: ["Vibrant colors", "Durable craftsmanship", "Gift wrapping available"]
        },
        {
            id: 'PE-04',
            category: "Wedding Memories",
            title: "A Portrait of Grace",
            price: "₹ 2,499",
            imageBase: "images/carousel-23.webp", // Flat photo
            imageTexture: "images/carousel-23.webp", // Detailed texture (stand-in)
            description: "Your favorite portrait, reimagined with delicate threadwork. Brings a unique texture and depth to your memories.",
            highlights: ["Artistic interpretation", "High-quality threads", "Unique home decor"]
        },
        {
            id: 'PE-05',
            category: "Wedding Memories",
            title: "Just Married Moments",
            price: "₹ 2,499",
            imageBase: "images/carousel-24.webp", // Flat photo
            imageTexture: "images/carousel-24.webp", // Detailed texture (stand-in)
            description: "Preserve the joy of your special day in a unique way. A stunning addition to any gallery wall.",
            highlights: ["Modern embroidery style", "Eco-friendly materials", "Fast shipping"]
        },
        {
            id: 'PE-06',
            category: "Family Heirlooms",
            title: "Generations Entwined",
            price: "₹ 2,499",
            imageBase: "images/col2.webp", // Flat photo
            imageTexture: "images/col2.webp", // Detailed texture (stand-in)
            description: "A beautiful embroidered portrait celebrating family bonds and cherished memories. Crafted carefully to resemble classic vintage artistry.",
            highlights: ["Vintage embroidery styling", "Detailed facial threadwork", "Generational keepsake"]
        },
        {
            id: 'PE-07',
            category: "Milestone Memories",
            title: "Cherished Milestones",
            price: "₹ 2,499",
            imageBase: "images/col3.webp", // Flat photo
            imageTexture: "images/col3.webp", // Detailed texture (stand-in)
            description: "Commemorate significant achievements, anniversaries, or special celebrations with a personalized embroidered art piece that stands out.",
            highlights: ["Custom celebration text", "Bright & festive threads", "Elegant wooden framing"]
        },
    ];

    const handleViewProduct = (product) => {
        navigate('/product-view', { state: { product } });
    };

    return (
        <>
            <section className="pe-section">
                <div style={{ padding: "0 5%", maxWidth: "1200px", margin: "0 auto", marginTop: "20px" }}>
                    <BackButton />
                </div>
                <div className="pe-header">
                    <h2 className="pe-main-title">❖ Photo Embroidery Collection ❖</h2>
                    <div className="pe-thread-line"></div>
                    <p className="pe-subtitle">
                        Transform your cherished photos into timeless embroidered art.
                        A unique blend of photography and handcrafted texture.
                    </p>
                </div>

                <div className="pe-grid">
                    {products.map((product) => (
                        <EmbroideryCard key={product.id} product={product} onViewClick={handleViewProduct} />
                    ))}

                </div>
            </section>
            <CustomiseSection />
        </>
    );
}

